"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import ContentDisplay from "@/components/waves/drops/ContentDisplay";
import { buildProcessedContent } from "@/components/waves/drops/media-utils";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ImageScale } from "@/helpers/image.helpers";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import BoostedDropLinkPreview from "./BoostedDropLinkPreview";
import { extractFirstUrl, removePreviewUrlFromContent } from "./extractStandaloneUrl";
type BoostedDropPart = ApiDrop["parts"][number];
type BoostedDropMediaItem = BoostedDropPart["media"][number];
type OverflowElements = {
  readonly container: HTMLDivElement;
  readonly preview: HTMLDivElement;
  readonly textMeasure: HTMLDivElement;
  readonly wrapper: HTMLDivElement;
};
const HOME_ASPECT_RATIO_CLASSES = "tw-aspect-[2/1] sm:tw-aspect-[5/4] md:tw-aspect-[8/5] lg:tw-aspect-[5/4] xl:tw-aspect-[8/5]";
const CHAT_MAX_HEIGHT_CLASSES = "tw-max-h-[11rem] md:tw-max-h-[12rem]";
const CHAT_PREVIEW_CONTAINER_CLASSES = `tw-relative tw-flex ${CHAT_MAX_HEIGHT_CLASSES} tw-w-full tw-flex-col tw-items-stretch tw-justify-stretch tw-gap-3 tw-overflow-hidden tw-rounded-xl`;
const CHAT_TEXT_CONTAINER_CLASSES = `tw-relative tw-flex ${CHAT_MAX_HEIGHT_CLASSES} tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-px-4 tw-py-4 sm:tw-px-6`;
const HOME_PREVIEW_CONTAINER_CLASSES = `tw-relative tw-flex ${HOME_ASPECT_RATIO_CLASSES} tw-w-full tw-flex-col tw-items-stretch tw-justify-stretch tw-gap-3 tw-overflow-hidden tw-rounded-xl`;
const HOME_TEXT_CONTAINER_CLASSES = `tw-relative tw-flex ${HOME_ASPECT_RATIO_CLASSES} tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-px-6 tw-pb-6 sm:tw-pb-0 tw-pt-4 sm:tw-pt-16 md:tw-pt-12`;
const CHAT_PREVIEW_WRAPPER_BASE_CLASSES = "tw-relative tw-z-10 tw-grid tw-h-full tw-w-full tw-min-w-0 tw-max-w-full tw-grid-rows-[minmax(0,1fr)_auto]";
const HOME_PREVIEW_WRAPPER_BASE_CLASSES = "tw-relative tw-z-10 tw-grid tw-h-full tw-w-full tw-min-w-0 tw-max-w-full tw-grid-rows-[minmax(0,1fr)_auto]";
const CHAT_TEXT_WRAPPER_CLASSES = "tw-relative tw-z-10 tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-items-center tw-justify-center";
const HOME_TEXT_WRAPPER_CLASSES = "tw-relative tw-z-10 tw-flex tw-h-full tw-w-full tw-min-w-0 tw-max-w-full tw-items-center tw-justify-center";
const WITH_PREVIEW_CONTENT_CLASS_NAME = "tw-flex tw-w-full tw-max-w-full tw-flex-col tw-items-start tw-gap-1 tw-break-words tw-px-3 tw-pb-3 sm:tw-px-4 sm:tw-pb-4 tw-tracking-[0.01em] tw-font-normal tw-text-iron-300";
const HOME_TEXT_CONTENT_CLASS_NAME = "tw-flex tw-w-fit tw-max-w-full tw-flex-col tw-items-start tw-gap-1 tw-break-words tw-tracking-[0.01em] tw-font-normal tw-text-iron-300";
const WITH_PREVIEW_TEXT_CLAMP_CLASS = "tw-line-clamp-2 sm:tw-line-clamp-3";
const WITHOUT_PREVIEW_TEXT_CLAMP_CLASS = "tw-line-clamp-6";
const CONTENT_TEXT_BASE_CLASSES = "tw-max-w-full tw-break-words tw-tracking-[0.01em] tw-font-normal";
const MAX_OVERFLOW_MEASUREMENT_ATTEMPTS = 12;
const OVERFLOW_MEASUREMENT_INTERVAL_MS = 200;
interface BoostedDropCardHomeContentProps {
  readonly part?: BoostedDropPart | undefined;
  readonly isChatVariant: boolean;
}
type BoostedDropOverflowMeasurementOptions = {
  readonly hasPreview: boolean;
  readonly hasTextContent: boolean;
  readonly isChatVariant: boolean;
};
const getContentContainerClasses = ({
  hasPreview,
  isChatVariant
}: {
  readonly hasPreview: boolean;
  readonly isChatVariant: boolean;
}): string => {
  if (isChatVariant) {
    return hasPreview ? CHAT_PREVIEW_CONTAINER_CLASSES : CHAT_TEXT_CONTAINER_CLASSES;
  }
  return hasPreview ? HOME_PREVIEW_CONTAINER_CLASSES : HOME_TEXT_CONTAINER_CLASSES;
};
const getContentWrapperClasses = ({
  hasPreview,
  hasTextContent,
  isChatVariant
}: {
  readonly hasPreview: boolean;
  readonly hasTextContent: boolean;
  readonly isChatVariant: boolean;
}): string => {
  if (!hasPreview) {
    return isChatVariant ? CHAT_TEXT_WRAPPER_CLASSES : HOME_TEXT_WRAPPER_CLASSES;
  }
  const baseClasses = isChatVariant ? CHAT_PREVIEW_WRAPPER_BASE_CLASSES : HOME_PREVIEW_WRAPPER_BASE_CLASSES;
  const gapClasses = hasTextContent ? "tw-gap-2 sm:tw-gap-3" : "";
  return gapClasses ? `${baseClasses} ${gapClasses}` : baseClasses;
};
const getContentClassName = ({
  hasPreview
}: {
  readonly hasPreview: boolean;
}): string => {
  if (hasPreview) {
    return WITH_PREVIEW_CONTENT_CLASS_NAME;
  }
  return HOME_TEXT_CONTENT_CLASS_NAME;
};
const getContentTextClassName = (hasPreview: boolean): string => {
  const clampClasses = hasPreview ? WITH_PREVIEW_TEXT_CLAMP_CLASS : WITHOUT_PREVIEW_TEXT_CLAMP_CLASS;
  return `${clampClasses} ${CONTENT_TEXT_BASE_CLASSES}`;
};
const getOverflowElements = ({
  container,
  preview,
  textMeasure,
  wrapper
}: {
  readonly container: HTMLDivElement | null;
  readonly preview: HTMLDivElement | null;
  readonly textMeasure: HTMLDivElement | null;
  readonly wrapper: HTMLDivElement | null;
}): OverflowElements | null => {
  if (!container || !preview || !textMeasure || !wrapper) {
    return null;
  }
  return {
    container,
    preview,
    textMeasure,
    wrapper
  };
};
const shouldHideOverflowText = ({
  container,
  preview,
  textMeasure,
  wrapper
}: OverflowElements): boolean => {
  const containerHeight = container.getBoundingClientRect().height;
  const previewHeight = preview.getBoundingClientRect().height;
  const previewScrollHeight = preview.scrollHeight;
  const previewClientHeight = preview.clientHeight;
  const textHeight = textMeasure.getBoundingClientRect().height;
  const computedStyles = getComputedStyle(wrapper);
  const rowGap = Number.parseFloat(computedStyles.rowGap || "0");
  const previewOverflowing = previewScrollHeight > previewClientHeight + 1;
  const totalHeight = Math.max(previewHeight, previewScrollHeight) + textHeight + rowGap;
  return previewOverflowing || totalHeight > containerHeight + 1;
};
const observeIfPresent = (observer: ResizeObserver, element: HTMLDivElement | null) => {
  if (element) {
    observer.observe(element);
  }
};
const cancelAnimationFrameIfNeeded = (frame: number | undefined) => {
  if (typeof frame === "number") {
    globalThis.cancelAnimationFrame(frame);
  }
};
const useBoostedDropPreviewContent = part => {
  const $ = _c(17);
  part?.content;
  const t0 = part?.content;
  let t1;
  if ($[0] !== t0) {
    t1 = extractFirstUrl(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewUrl = t1;
  let t2;
  if ($[2] !== part?.content || $[3] !== previewUrl) {
    t2 = () => previewUrl ? removePreviewUrlFromContent(part?.content, previewUrl) : part?.content;
    $[2] = part?.content;
    $[3] = previewUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  part?.content;
  let t3;
  if ($[5] !== t2) {
    t3 = t2();
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const cleanedContent = t3;
  part?.media;
  const t4 = cleanedContent ?? null;
  const t5 = part?.media;
  const t6 = previewUrl ? "" : "View drop...";
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = buildProcessedContent(t4, t5, t6);
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const previewContent = t7;
  let t8;
  if ($[11] !== cleanedContent) {
    t8 = cleanedContent && cleanedContent.trim().length > 0;
    $[11] = cleanedContent;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const hasTextContent = Boolean(t8);
  let t9;
  if ($[13] !== hasTextContent || $[14] !== previewContent || $[15] !== previewUrl) {
    t9 = {
      hasTextContent,
      previewContent,
      previewUrl
    };
    $[13] = hasTextContent;
    $[14] = previewContent;
    $[15] = previewUrl;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};
const useOverflowResizeTracking = t0 => {
  const $ = _c(7);
  const {
    enabled,
    measureOverflow,
    contentContainerRef,
    previewRef,
    textMeasureRef
  } = t0;
  let t1;
  let t2;
  if ($[0] !== contentContainerRef || $[1] !== enabled || $[2] !== measureOverflow || $[3] !== previewRef || $[4] !== textMeasureRef) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      const frame = globalThis.requestAnimationFrame(() => {
        measureOverflow();
      });
      if (typeof ResizeObserver === "undefined") {
        const handleResize = () => measureOverflow();
        globalThis.addEventListener("resize", handleResize);
        return () => {
          globalThis.removeEventListener("resize", handleResize);
          cancelAnimationFrameIfNeeded(frame);
        };
      }
      const observer = new ResizeObserver(() => measureOverflow());
      observeIfPresent(observer, contentContainerRef.current);
      observeIfPresent(observer, previewRef.current);
      observeIfPresent(observer, textMeasureRef.current);
      return () => {
        observer.disconnect();
        cancelAnimationFrameIfNeeded(frame);
      };
    };
    t2 = [contentContainerRef, enabled, measureOverflow, previewRef, textMeasureRef];
    $[0] = contentContainerRef;
    $[1] = enabled;
    $[2] = measureOverflow;
    $[3] = previewRef;
    $[4] = textMeasureRef;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
};
const useOverflowMeasurementRetry = t0 => {
  const $ = _c(4);
  const {
    enabled,
    measureOverflow
  } = t0;
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== measureOverflow) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      let attempts = 0;
      const intervalId = globalThis.setInterval(() => {
        measureOverflow();
        attempts = attempts + 1;
        attempts;
        if (attempts >= MAX_OVERFLOW_MEASUREMENT_ATTEMPTS) {
          globalThis.clearInterval(intervalId);
        }
      }, OVERFLOW_MEASUREMENT_INTERVAL_MS);
      return () => {
        if (typeof intervalId === "number") {
          globalThis.clearInterval(intervalId);
        }
      };
    };
    t2 = [enabled, measureOverflow];
    $[0] = enabled;
    $[1] = measureOverflow;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
};
const useBoostedDropOverflowMeasurement = t0 => {
  const $ = _c(11);
  const {
    hasPreview,
    hasTextContent
  } = t0;
  const contentContainerRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const previewRef = useRef(null);
  const textMeasureRef = useRef(null);
  const [hideOverflowText, setHideOverflowText] = useState(false);
  const [hasMeasuredOverflow, setHasMeasuredOverflow] = useState(false);
  const shouldMeasureOverflow = hasPreview && hasTextContent;
  let t1;
  if ($[0] !== shouldMeasureOverflow) {
    t1 = () => {
      if (!shouldMeasureOverflow) {
        return;
      }
      const elements = getOverflowElements({
        container: contentContainerRef.current,
        preview: previewRef.current,
        textMeasure: textMeasureRef.current,
        wrapper: contentWrapperRef.current
      });
      if (!elements) {
        setHasMeasuredOverflow(false);
        setHideOverflowText(false);
        return;
      }
      setHasMeasuredOverflow(true);
      setHideOverflowText(shouldHideOverflowText(elements));
    };
    $[0] = shouldMeasureOverflow;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const measureOverflow = t1;
  let t2;
  if ($[2] !== measureOverflow || $[3] !== shouldMeasureOverflow) {
    t2 = {
      contentContainerRef,
      enabled: shouldMeasureOverflow,
      measureOverflow,
      previewRef,
      textMeasureRef
    };
    $[2] = measureOverflow;
    $[3] = shouldMeasureOverflow;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useOverflowResizeTracking(t2);
  let t3;
  if ($[5] !== measureOverflow || $[6] !== shouldMeasureOverflow) {
    t3 = {
      enabled: shouldMeasureOverflow,
      measureOverflow
    };
    $[5] = measureOverflow;
    $[6] = shouldMeasureOverflow;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useOverflowMeasurementRetry(t3);
  const shouldShowTextContent = !hasPreview || hasTextContent && (!shouldMeasureOverflow || !hasMeasuredOverflow || !hideOverflowText);
  let t4;
  if ($[8] !== shouldMeasureOverflow || $[9] !== shouldShowTextContent) {
    t4 = {
      contentContainerRef,
      contentWrapperRef,
      previewRef,
      shouldMeasureOverflow,
      shouldShowTextContent,
      textMeasureRef
    };
    $[8] = shouldMeasureOverflow;
    $[9] = shouldShowTextContent;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const BoostedDropCardHomeMedia = memo(t0 => {
  const $ = _c(6);
  const {
    isChatVariant,
    media
  } = t0;
  const t1 = `tw-relative ${HOME_ASPECT_RATIO_CLASSES} ${isChatVariant ? CHAT_MAX_HEIGHT_CLASSES : ""} tw-w-full tw-overflow-hidden tw-rounded-xl`;
  let t2;
  if ($[0] !== media.mime_type || $[1] !== media.url) {
    t2 = <div className="tw-relative tw-h-full tw-w-full"><div className="tw-relative tw-z-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-xl tw-transition-transform tw-duration-700 group-hover:tw-scale-[1.02]"><div className="tw-relative tw-h-full tw-w-full tw-overflow-hidden"><DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageScale={ImageScale.AUTOx450} imageObjectPosition="center" /></div></div></div>;
    $[0] = media.mime_type;
    $[1] = media.url;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <div data-testid="boosted-drop-media-frame" className={t1}>{t2}</div>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
});
BoostedDropCardHomeMedia.displayName = "BoostedDropCardHomeMedia";
const BoostedDropCardTextSection = memo(t0 => {
  const $ = _c(45);
  const {
    isChatVariant,
    part
  } = t0;
  const {
    hasTextContent,
    previewContent,
    previewUrl
  } = useBoostedDropPreviewContent(part);
  const hasPreview = Boolean(previewUrl);
  let t1;
  if ($[0] !== hasPreview || $[1] !== hasTextContent || $[2] !== isChatVariant) {
    t1 = {
      hasPreview,
      hasTextContent,
      isChatVariant
    };
    $[0] = hasPreview;
    $[1] = hasTextContent;
    $[2] = isChatVariant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    contentContainerRef,
    contentWrapperRef,
    previewRef,
    shouldMeasureOverflow,
    shouldShowTextContent,
    textMeasureRef
  } = useBoostedDropOverflowMeasurement(t1);
  let t2;
  if ($[4] !== hasPreview || $[5] !== isChatVariant) {
    t2 = getContentContainerClasses({
      hasPreview,
      isChatVariant
    });
    $[4] = hasPreview;
    $[5] = isChatVariant;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const contentContainerClasses = t2;
  let t3;
  if ($[7] !== hasPreview || $[8] !== hasTextContent || $[9] !== isChatVariant) {
    t3 = getContentWrapperClasses({
      hasPreview,
      hasTextContent,
      isChatVariant
    });
    $[7] = hasPreview;
    $[8] = hasTextContent;
    $[9] = isChatVariant;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const contentWrapperClasses = t3;
  let t4;
  if ($[11] !== hasPreview) {
    t4 = getContentClassName({
      hasPreview
    });
    $[11] = hasPreview;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const contentClassName = t4;
  let t5;
  if ($[13] !== hasPreview) {
    t5 = getContentTextClassName(hasPreview);
    $[13] = hasPreview;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const contentTextClassName = t5;
  let t6;
  if ($[15] !== previewRef || $[16] !== previewUrl) {
    t6 = previewUrl && <div className="tw-min-h-0 tw-overflow-hidden" ref={previewRef}><BoostedDropLinkPreview href={previewUrl} variant="home" /></div>;
    $[15] = previewRef;
    $[16] = previewUrl;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== contentClassName || $[19] !== contentTextClassName || $[20] !== hasTextContent || $[21] !== previewContent || $[22] !== previewUrl || $[23] !== shouldMeasureOverflow || $[24] !== textMeasureRef) {
    t7 = shouldMeasureOverflow && previewUrl && hasTextContent && <div ref={textMeasureRef} aria-hidden="true" className="tw-pointer-events-none tw-invisible tw-absolute tw-left-0 tw-right-0 tw-top-0"><ContentDisplay content={previewContent} shouldClamp={false} className={contentClassName} textClassName={contentTextClassName} /></div>;
    $[18] = contentClassName;
    $[19] = contentTextClassName;
    $[20] = hasTextContent;
    $[21] = previewContent;
    $[22] = previewUrl;
    $[23] = shouldMeasureOverflow;
    $[24] = textMeasureRef;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== contentClassName || $[27] !== contentTextClassName || $[28] !== previewContent || $[29] !== shouldShowTextContent) {
    t8 = shouldShowTextContent && <ContentDisplay content={previewContent} shouldClamp={false} className={contentClassName} textClassName={contentTextClassName} />;
    $[26] = contentClassName;
    $[27] = contentTextClassName;
    $[28] = previewContent;
    $[29] = shouldShowTextContent;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== contentWrapperClasses || $[32] !== contentWrapperRef || $[33] !== t6 || $[34] !== t7 || $[35] !== t8) {
    t9 = <div className={contentWrapperClasses} ref={contentWrapperRef}>{t6}{t7}{t8}</div>;
    $[31] = contentWrapperClasses;
    $[32] = contentWrapperRef;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== hasPreview || $[38] !== isChatVariant) {
    t10 = !hasPreview && !isChatVariant && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0.5 tw-bottom-0 tw-z-20 tw-h-6 tw-bg-gradient-to-b tw-from-black/0 tw-to-black/70 sm:tw-h-10 md:tw-h-12" />;
    $[37] = hasPreview;
    $[38] = isChatVariant;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== contentContainerClasses || $[41] !== contentContainerRef || $[42] !== t10 || $[43] !== t9) {
    t11 = <div data-testid="boosted-drop-content-frame" className={contentContainerClasses} ref={contentContainerRef}>{t9}{t10}</div>;
    $[40] = contentContainerClasses;
    $[41] = contentContainerRef;
    $[42] = t10;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
});
BoostedDropCardTextSection.displayName = "BoostedDropCardTextSection";
const BoostedDropCardHomeContent = memo(t0 => {
  const $ = _c(6);
  const {
    part,
    isChatVariant
  } = t0;
  const media = part?.media[0];
  if (!media) {
    let t1;
    if ($[0] !== isChatVariant || $[1] !== part) {
      t1 = <BoostedDropCardTextSection isChatVariant={isChatVariant} part={part} />;
      $[0] = isChatVariant;
      $[1] = part;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== isChatVariant || $[4] !== media) {
    t1 = <BoostedDropCardHomeMedia isChatVariant={isChatVariant} media={media} />;
    $[3] = isChatVariant;
    $[4] = media;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
});
BoostedDropCardHomeContent.displayName = "BoostedDropCardHomeContent";
export default BoostedDropCardHomeContent;